/*
 * Shared query definitions.
 *
 * ONE CACHE KEY MUST MEAN ONE SHAPE. Three pages fetched the organization under
 * `["organization", id]` and two of them stored a different thing:
 *
 *   UserDetail, Screenshots   r.data.data.organization   the organization
 *   Settings                  r.data.data               { organization, storage,
 *                                                         trackingSchedules }
 *
 * TanStack caches by key, not by call site, so whichever page mounted first won
 * and the others read a shape they did not expect. With staleTime at 30s the
 * symptom was intermittent and looked like a loading bug: open Screenshots,
 * click Settings within half a minute, and Settings read `data.organization`
 * off an object that WAS the organization — undefined — so it sat on
 * "Loading…" and never refetched, because the cached entry was still fresh.
 * Wait past the stale window and it worked, which is why it came and went.
 *
 * Defining the query once removes the chance to disagree. Every consumer now
 * gets the whole envelope and picks what it needs from it.
 */
import { organizationService, appVersionService } from "@/lib/api.js";

/**
 * The organization, its effective storage state, and its tracking schedules.
 *
 * Spread into useQuery: `useQuery({ ...organizationQuery(id) })`.
 */
export function organizationQuery(organizationId) {
    return {
        queryKey: ORGANIZATION_KEY(organizationId),
        enabled: Boolean(organizationId),
        queryFn: () =>
            organizationService.getOrganization(organizationId).then((r) => r.data?.data ?? {}),
    };
}

/** The key on its own, for invalidation after a save. */
export const ORGANIZATION_KEY = (organizationId) => ["organization", organizationId];

/*
 * The newest desktop version published in the App Versions catalog — the single
 * source of truth for the dashboard's "up to date / update available" check.
 * Replaces a hardcoded LATEST_DESKTOP constant that silently went stale. Null
 * while loading or when nothing is published; callers treat null as "don't nag"
 * (compareVersion against a null latest is always up-to-date).
 */
export function latestDesktopVersionQuery() {
    return {
        queryKey: ["app-latest-version"],
        queryFn: () => appVersionService.latestVersion().then((r) => r.data?.data?.version ?? null),
        staleTime: 5 * 60 * 1000,
    };
}
